<template>
	<div class="outdoor-delivery">
		<h2
			class="outdoor-delivery__title insicht insicht--fade-up"
			v-html="$t('outdoordelivery.title')"
		/>

		<div class="outdoor-delivery__wrapper">
			<div
        ref="mapContainer"
        class="outdoor-delivery__map insicht insicht--fade-up"
      />
			<div class="outdoor-delivery__content insicht insicht--fade-up">
				<template v-if="!geolocationSort">
					<Button
						class="outdoor-delivery__geolocation"
						icon="geo-location"
						:text="$t('outdoordelivery.geolocation.button')"
						type="button"
						level="tertiary"
						arrow
						v-if="geolocationAvailable"
						@click="geolocationRequest(true)"
					/>
					<div
						v-for="region in outdoorDeliveryPointsByRegion"
						:key="region.code"
						class="outdoor-delivery__list"
					>
						<h3
							class="outdoor-delivery__list-title"
							v-html="$t(`orderbox.regions.${formatRegionCode(region.code)}`)"
						/>
						<Collapsible
							v-for="location in region.locations"
							:key="location.id"
							:location="location.id"
							ref="location"
							class="outdoor-delivery__item"
							:initial-open="false"
							@open="onLocationSelect(location)"
							@close="onLocationClose(location)"
						>
							<template slot="trigger">
								<a
									ref="locationAnchor"
									class="anchor"
								/>
								<h4
									class="outdoor-delivery__item-title"
									v-html="location.title"
								/>
							</template>

							<div class="outdoor-delivery__item-content">
								<div
									class="outdoor-delivery__item-hint"
									v-html="$t('outdoordelivery.locationHint')"
								/>
								<OrderBoxLocation
									v-for="channel in location.channels"
									:key="channel.code"
									:location="formatLocation(channel, location.postcode)"
									:shipping-method="shippingMethod"
									@click="onChannelSelect(channel, location)"
								/>
							</div>
						</Collapsible>
					</div>
				</template>

				<template v-else>
					<Button
						class="outdoor-delivery__geolocation"
						:text="$t('outdoordelivery.geolocation.back')"
						type="button"
						level="tertiary"
						arrow
						reverse
						@click="geolocationReset"
					/>
					<div
						class="outdoor-delivery__list"
					>
						<h3
							class="outdoor-delivery__list-title"
							v-html="$t('outdoordelivery.geolocation.title')"
						/>
						<Collapsible
							v-for="location in locationsEnhancedGeolocation"
							:key="location.id"
							:location="location.id"
							ref="location"
							class="outdoor-delivery__item"
							:initial-open="false"
							@open="onLocationSelect(location)"
							@close="onLocationClose(location)"
						>
							<template slot="trigger">
								<a
									ref="locationAnchor"
									class="anchor"
								/>
								<h4
									class="outdoor-delivery__item-title"
									v-html="location.title"
								/>
							</template>

							<div class="outdoor-delivery__item-content">
								<div
									class="outdoor-delivery__item-hint"
									v-html="$t('outdoordelivery.locationHint')"
								/>
								<OrderBoxLocation
									v-for="channel in location.channels"
									:key="channel.code"
									:location="formatLocation(channel, location.postcode)"
									:shipping-method="shippingMethod"
									@click="onChannelSelect(channel, location)"
								/>
							</div>
						</Collapsible>
					</div>
				</template>
			</div>
		</div>

		<Modal
			ref="modal"
			overlay-type="outdoor-delivery"
			:html-classes="['hide--header', 'show--modal']"
			narrow
			@close="onModalClose"
		>
			<div
				v-if="selectedChannel"
				class="overlay-dialog"
			>
				<h2
					class="overlay-dialog__title"
					v-html="$t('outdoordelivery.overlayTitle')"
				/>
				<OrderBoxLocation
					:location="formatLocation(selectedChannel, selectedLocation.postcode, locationOrderUberDirect)"
					:shipping-method="shippingMethod"
					no-click
				/>
				<GeneralMessage
					v-if="errors.time"
					:message="errors.time"
					type="error"
					@close="errors.time = ''"
				/>
				<OrderBoxTime
					v-if="location"
          :uber-direct="locationOrderUberDirect"
					:order-now="locationOrderNow"
					:order-now-available="locationOrderNowAvailable"
					:date="selectedDate"
					:dateMin="locationOrderMinDate.format('L')"
					:dateMax="locationOrderMaxDate.format('L')"
					:isDateDisabled="isDateDisabled.bind(this)"
					:time="selectedTime"
					:times="locationOrderTimes"
					:buttonText="$t('orderbox.deliveryButton')"
					:yielding-messages="yieldingMessages"
					@ordernowchanged="onLocationOrderNowChange"
					@datechanged="onLocationDateSelect"
					@timechanged="onLocationTimeSelect"
					@click="onOrder"
				/>
			</div>
		</Modal>
	</div>
</template>

<script>
import { Loader } from "@googlemaps/js-api-loader"
import { MarkerClusterer } from "@googlemaps/markerclusterer"
import scrrroll from 'scrrroll'
import { styles, getMarkerIcon, getMarkerLabel, getGeoIcon } from '@/helpers/googleMaps'
import GenericMixin from '@/mixins/GenericVueMixin'
import { mapGetters } from "vuex"

import Collapsible from '@/components/Collapsible'
import Modal from '@/components/Modal'
import OrderBoxLocation from '@/components/OrderBoxLocation'
import OrderBoxTime from '@/components/OrderBoxTime'
import Button from '@/components/Button'
import GeneralMessage from '@/components/GeneralMessage'
import { eventhub, EVENT_ACCOUNT_ISLOGGEDIN } from '@/store/eventhub'
import { toCamel } from '@/utils/convertcase'
import { dayjs, isSameDay, dayjsLocal } from "@/helpers/dayjs"
import MixinGoogleMaps from '@/mixins/MixinGoogleMaps'

export default {
	mixins: [
		GenericMixin,
    MixinGoogleMaps,
	],
	components: {
		Button,
		GeneralMessage,
		Collapsible,
		OrderBoxLocation,
		Modal,
		OrderBoxTime,
	},
	props: {
		shippingMethod: {
			type: String,
			default: 'delivery'
		}
	},
	data() {
		return {
			campaignId: this.$root.options.campaignId,
			loaded: false,
			notFound: false,
			selectedLocation: null,
			selectedChannel: null,
			errors: {
				zip: '',
				location: '',
				time: '',
				region: '',
			},
      mapElementClass: 'outdoor-delivery__map-element'
		}
	},
	computed: {
		...mapGetters("account", [
			"isLoggedIn",
			"accountUserDetail",
		]),
		...mapGetters("outdoordelivery", [
			"outdoorDeliveryPoints",
			"outdoorDeliveryPointsByRegion",
		]),
		...mapGetters('loader', [
			'isLoading',
		]),
		...mapGetters("location", [
			"locationsLoading",
			"locations",
			"location",
			"locationRegions",
			"locationOrderZip",
			"locationOrderMinDate",
			"locationOrderMaxDate",
			"locationOrderDisabledDates",
			"locationOrderTimes",
			"locationOrderDate",
			"locationOrderTime",
      "locationOrderUberDirect",
			"locationOrderNow",
			"locationOrderNowAvailable",
			"locationYieldingMessages",
			"locationClosedToday",
			"locationClosedTodayCoupon",
		]),
		loading: {
			set(value) {
				this.$store.dispatch('loader/setLoading', value)
				this.$emit('loading', value)
			},
			get() {
				return this.isLoading
			}
		},
		selectedDate: {
			get() {
				return this.locationOrderDate
			},
			set(value) {
				this.$store.dispatch('location/setOrderDate', { dateStr: value })
			}
		},
		selectedTime: {
			get() {
				return this.locationOrderTime
			},
			set(value) {
				this.$store.dispatch('location/setOrderTime', { timeStr: value })
			}
		},
		yieldingMessages() {
			return this.locationYieldingMessages
				.filter(m => !m.type || m.type == this.selectedShippingMethod)
				.map(m => m.content)
		},
    locations() {
      return this.outdoorDeliveryPoints ?? []
    },
	},
	methods: {
		formatLocation(location, zip, uberDirect) {
			let formatted = {
				image: location.image,
				name: location.name,
				code: location.code,
				brand: location.location.brand,
				region: location.location.region,
				address: location.location.address,
				tags: location.location.tags ?? [],
			}

			if (zip) {
				zip = location.zips.find(z => z.zip == zip)
				formatted = {
					...formatted,
					deliveryTime: zip?.deliveryTime,
					deliveryPrice: zip?.deliveryPrice,
					minDeliveryAmount: zip?.minDeliveryAmount,
          uberDirect: uberDirect != null ? uberDirect : !!zip?.uberDirect,
				}
			}

			return formatted
		},
		formatRegionCode(code) {
			return toCamel(code)
		},
		formatRegionCode(code) {
			return toCamel(code)
		},
		async selectLocation(location) {
			const l = this.$refs.location.find(l => l.$attrs.location === location.id)
			if (l) {
				const index = this.$refs.location.indexOf(l)
				const anchor = this.$refs.locationAnchor[index]

				await l.open()
				scrrroll.to(anchor)
			}
		},
		onLocationSelect(location) {
      this.activeLocation = location.id
			this.panToMarker(location.id)
			this.$refs.location.forEach(l => {
				if (l.$attrs.location !== location.id) {
					l.close()
				}
			})
		},
		onLocationClose(location) {
			if (this.activeLocation === location.id && this.$refs.location.every(l => !l.opened)) {
				this.panToAll()
			}
		},
		async onChannelSelect(channel, location) {
			this.selectedLocation = location
			this.selectedChannel = channel
			const currentZip = channel.zips.find(z => z.zip == location.postcode)

			await this.$store.dispatch('location/setLocation', { location: channel, deliveryMethod: this.shippingMethod, zip: currentZip, deliveryTime: currentZip.deliveryTime })
			this.selectedDate = this.locationOrderMinDate.format('L')
			this.selectedTime = this.locationOrderTimes[0]?.value

			this.$refs.modal.open()
		},
		onModalClose() {
			this.selectedLocation = null
			this.selectedChannel = null
			this.$store.dispatch('location/clearLocation')
		},
		onResize() {
			this.$el.style.setProperty('--map-height', `${this.$refs.mapContainer.clientHeight}px`)
		},
		isDateDisabled(date) {
			const dates = this.locationOrderDisabledDates
			const check = dayjsLocal(date)
			return dates.find(d => isSameDay(d, check))
		},
		onLocationOrderNowChange(value) {
			this.$store.dispatch('location/setOrderNow', { value })
		},
		onLocationDateSelect(date) {
			this.selectedDate = date

			if (this.selectedDate) {
				this.selectedTime = this.locationOrderTimes[0]?.value
			}
		},
		onLocationTimeSelect(time) {
			this.selectedTime = time
		},
		async onOrder(e) {
			let validDateTime = (this.locationOrderNow && this.locationOrderNowAvailable) || (this.selectedDate && this.selectedTime)
			if (this.location && validDateTime) {
				const zip = this.selectedLocation?.postcode || 0
				this.loading = true
				this.errors.time = ''
				try {
					await this.$store.dispatch('openCartAtChannel', {
						channel: this.location.code,
						orderNow: this.locationOrderNow && this.locationOrderNowAvailable,
						deliverDate: this.selectedDate,
						deliverTime: this.selectedTime,
						zip,
						shippingCode: this.shippingMethod,
						replaceOldCart: true,
						alternativeBrand: this.location.alternativeBrand,
						outdoorDeliveryId: this.selectedLocation?.id
					})
					// we should get redirected, so non of this code does matter below here
					this.$refs.modal.close()
				} catch (err) {
          if (err.isMaintenanceError) {
            this.onMaintenanceMode({ callback: () => this.onOrder(e), retry: true })
          } else {
            // TODO: handle a possible error here
            if (err.response && err.response.data.message) {
              this.errors.time = err.response.data.errors && err.response.data.errors.deliverTime
                ? err.response.data.errors.deliverTime.join()
                : err.response.data.message
            } else {
              throw err
            }
          }
					this.loading = false
				}

			} else if (!validDateTime) {
				this.errors.time = this.$t('orderbox.errorTime')
			}
		},
	},
	async created() {
		this.loading = true
		try {
			await this.$store.dispatch('outdoordelivery/loadOutdoorDeliveryPoints', { campaignId: this.campaignId })
			if (!this.outdoorDeliveryPoints.length) {
				this.notFound = true
			} else {
				await this.setupGoogleMaps();
				await this.geolocationCheck();
			}
		}
		catch (err) {
      if (err.isMaintenanceError) {
        this.onMaintenanceMode()
      } else {
        this.notFound = true
      }
		}
		this.loaded = true
		this.loading = false
	},
	mounted() {
		eventhub.$on(EVENT_ACCOUNT_ISLOGGEDIN, this.onIsLoggedIn)
		window.addEventListener('resize', this.onResize)
		this.onResize()
	},
	destroyed() {
		eventhub.$off(EVENT_ACCOUNT_ISLOGGEDIN, this.onIsLoggedIn)
		window.removeEventListener('resize', this.onResize)
	}
}
</script>
